import React, { useContext, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  makeStyles, Typography, Button,
} from '@material-ui/core';

// import PropTypes from 'prop-types';
import {
  useMsal, useAccount,
} from '@azure/msal-react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { loginRequest } from '../../config/authConfig';
import { isDevAuthBypass, DEV_BYPASS_EMAIL, DEV_BYPASS_JWT } from '../../config/config';
import { LoggedIn, LoggedOut } from '../Common/AuthTemplate';
import AppContext from '../../state/AppContext';
import { fetchStaticToken, swallowMsalErrors } from '../../helpers/authentication';
import api from '../../config/api';
import { isValidatingCustomerState } from '../../state/apiRequestStatus';
import { jwtTokenState, validatedCustomerState, devBypassEmailState } from '../../state/apiData';

const useStyles = makeStyles(() => ({
  btnLogin: {
    padding: '0 5px',
    margin: '0 30px',
    color: '#ffffff',
    fontSize: '14px',
  },
  emailWrapper: {
    flex: '1 0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.6,
  },
}));

const HeaderLogin = () => {
  const intl = useIntl();
  const {
    setCustomerNeedsSignup,
    setCustomerNotValid,
  } = useContext(AppContext);

  const setValidatedCustomer = useSetRecoilState(validatedCustomerState);
  const [isValidatingCustomer, setIsValidatingCustomer] = useRecoilState(isValidatingCustomerState);
  const [jwtToken, setJwtToken] = useRecoilState(jwtTokenState);
  const [devEmail, setDevEmail] = useRecoilState(devBypassEmailState);

  const classes = useStyles();
  const {
    instance,
    accounts,
    inProgress,
  } = useMsal();

  const account = useAccount(accounts[0] || {
    idTokenClaims: {
      emails: [],
    },
  });

  // Courriel courant : connexion simulée (dev) ou compte MSAL réel.
  const currentEmail = (isDevAuthBypass && devEmail)
    ? devEmail
    : (account && account.idTokenClaims.emails[0]);

  // Acquisition du jeton réel : uniquement quand un vrai compte MSAL existe.
  useEffect(() => {
    if (accounts.length > 0 && account) {
      instance.acquireTokenSilent({
        scopes: ['openid'],
        account,
      })
        .then((response) => {
          if (response) {
            setJwtToken(response.idToken);
          }
        })
        .catch(swallowMsalErrors);
    }
  }, [account, instance]);

  // DEV ONLY: connexion simulée -> pousse le jeton sentinelle, ce qui déclenche
  // la validation client ci-dessous (effet sur [jwtToken]).
  useEffect(() => {
    if (isDevAuthBypass && devEmail) {
      setJwtToken(DEV_BYPASS_JWT);
    }
  }, [devEmail]);

  useEffect(async () => {
    setValidatedCustomer(null);
    setCustomerNeedsSignup(false);
    setCustomerNotValid(false);

    if (currentEmail && jwtToken) {
      if (window && window.gtag) {
        window.gtag('event', 'login', {
          method: 'Email',
        });
      }
      setIsValidatingCustomer(true);
      const responseValidation = await fetchStaticToken({
        url: api.customerValidation,
        method: 'POST',
        body: {
          email: currentEmail,
          jwt: jwtToken,
        },
      });

      const validation = await responseValidation.json();
      if (responseValidation.status === 200) {
        setValidatedCustomer(validation);
        return setIsValidatingCustomer(false);
      }

      if (responseValidation.status === 400) {
        if (validation.code === 'EmailNotExistsError') {
          setCustomerNeedsSignup(true);
        }
      }

      setCustomerNotValid(true);
      return setIsValidatingCustomer(false);
    }
    return null;
  }, [jwtToken]);

  const onLoginClick = () => {
    if (isDevAuthBypass) {
      setDevEmail(DEV_BYPASS_EMAIL);
    } else {
      instance.loginPopup(loginRequest(intl.locale)).catch(swallowMsalErrors);
    }
  };

  const onLogoutClick = () => {
    if (isDevAuthBypass && devEmail) {
      setDevEmail(null);
      setJwtToken(null);
      setValidatedCustomer(null);
    } else {
      instance.logout();
    }
  };

  return (
    <>
      <LoggedOut>
        <Button
          className={classes.btnLogin}
          onClick={onLoginClick}
          disabled={inProgress === 'login' || isValidatingCustomer}
        >
          {inProgress === 'login' || isValidatingCustomer
            ? <FormattedMessage id="LoginHeader.loggingIn" />
            : <FormattedMessage id="LoginHeader.login" />}
        </Button>
      </LoggedOut>

      <LoggedIn>
        {currentEmail
          && (
            <Typography component="span" className={classes.emailWrapper}>
              {currentEmail || ''}
            </Typography>
          )}
        <Typography component="span">
          <Button
            className={classes.btnLogin}
            onClick={onLogoutClick}
          >
            <FormattedMessage id="LoginHeader.logout" />
          </Button>
        </Typography>
      </LoggedIn>
    </>
  );
};
HeaderLogin.propTypes = {};

export default HeaderLogin;
